// One material / work item line in the estimate editor, expanding into its full editor.
//
// Everything here is live: the Product and Manufacturer chips come from this line's own alternate
// catalogue items and apply by swapping the line's item; the Supplier chips come from the mapped,
// priced supplier rows for THIS item and apply by re-pricing the line from that supplier. Nothing
// renders from a static list, so a chip that appears is a chip that does something.
//
// The two lookups are gated on `open`. A generated option carries 60+ lines, and firing them on
// mount would mean ~120 round trips to draw a list nobody has expanded yet.
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SearchableSelect, { type SearchableSelectOption } from '@/components/UI/SearchableSelect';
import {
  type BasisConformance, CONFORMANCE_CLASS, CONFORMANCE_ROW,
} from '@/components/Estimates/basisConformance';

import {
  type EstimateLine,
  type MaterialItemDetail,
  type OverrideMaterialLineInput,
  useGetAlternateVariantsQuery,
  useGetSuppliersForLineQuery,
  useOverrideMaterialLineMutation,
  useSetLineSupplierMutation,
  useSwapMaterialLineVariantMutation,
} from '@/redux/internal-services/estimatesApi';

const money = (n?: number | null, currency = 'USD') =>
  n === null || n === undefined
    ? '—'
    : new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);

// Quantities are computed (required x (1 + waste), then divided by a pack size), so they arrive as
// full binary floats — 36.73 LF of ridge cap at 10% waste rendered as "40.403999999999996 LF".
// Three decimals is past anything a roof is measured to; trailing zeros go so a whole number reads
// as one.
const qty = (n?: number | null) =>
  n === null || n === undefined ? '—' : String(Number(n.toFixed(3)));

/** One cell of the quantity ladder: caption over value, so the grid lines up whether the value is
 *  an input, a toggle pair or a derived number. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="mb-0.5 truncate text-ui-text3">{label}</p>
      {children}
    </div>
  );
}

interface Props {
  line: EstimateLine;
  detail?: MaterialItemDetail;
  currency: string;
  /** Whether this line got the supplier/manufacturer the option was created against — `null` when
   *  the basis makes no demand of it. See basisConformance.ts. */
  conformance: BasisConformance | null;
  /** What the option asked for, for the explanation line: a supplier or manufacturer code. */
  basisWanted: string | null;
  open: boolean;
  onToggle: () => void;
  commit: (run: () => Promise<unknown>) => Promise<void>;
  autosaveReason: string;
  /** Frozen estimate (an approved/prepared proposal owns it): show values, accept no edits. */
  readOnly?: boolean;
}

export default function ItemLineRow({
  line, detail, currency, conformance, basisWanted, open, onToggle, commit, autosaveReason, readOnly = false,
}: Props) {
  const { t } = useTranslation();
  const m = line.material;
  const lineCode = line.estimate_line_id;

  const [overrideMaterialLine] = useOverrideMaterialLineMutation();
  const [swapMaterialLineVariant] = useSwapMaterialLineVariantMutation();
  const [setLineSupplier] = useSetLineSupplierMutation();

  const { data: alternates } = useGetAlternateVariantsQuery(lineCode, { skip: !open || !m?.product_code });
  const { data: lineSuppliers } = useGetSuppliersForLineQuery(lineCode, { skip: !open || !m?.product_code });

  // Wastage is stored as a PERCENT (10 = 10%), the same figure the user typed — see
  // `override_material_line`, which writes `wastage.percent` straight through. Treating it as a
  // fraction and scaling by 100 rendered a 10% allowance as "1000". The control still reads the
  // METHOD rather than assuming a percentage, so a FIXED_QUANTITY line shows its quantity.
  const isFixedWaste = m?.wastage?.method === 'FIXED_QUANTITY';
  const serverWasteValue = String(
    isFixedWaste ? m?.wastage?.quantity ?? 0 : m?.wastage?.percent ?? 0,
  );
  const serverQuantity = String(m?.required_quantity ?? '');
  const serverUnitPrice = String(m?.unit_cost ?? '');

  const [wasteMode, setWasteMode] = useState<'percent' | 'fixed'>(isFixedWaste ? 'fixed' : 'percent');
  const [wasteValue, setWasteValue] = useState(serverWasteValue);
  const [quantity, setQuantity] = useState(serverQuantity);
  const [unitPrice, setUnitPrice] = useState(serverUnitPrice);

  // These three are typed into, so they are local state — but the server can change them WITHOUT
  // the user touching them: picking a supplier re-prices the line, and a quantity override
  // re-derives the rest. Seeding the state once left the price box showing the old $42 while the
  // row beside it priced at the new $40. The server value wins whenever it moves.
  useEffect(() => { setQuantity(serverQuantity); }, [serverQuantity]);
  useEffect(() => { setUnitPrice(serverUnitPrice); }, [serverUnitPrice]);
  useEffect(() => {
    setWasteMode(isFixedWaste ? 'fixed' : 'percent');
    setWasteValue(serverWasteValue);
  }, [isFixedWaste, serverWasteValue]);

  const saveOverride = (patch: Partial<OverrideMaterialLineInput>) => commit(async () => {
    await overrideMaterialLine({
      estimate_material_line_code: lineCode,
      override_reason: autosaveReason,
      ...patch,
    }).unwrap();
  });

  // The manufacturers available for this line: the current item's, plus every alternate's. Current
  // first, so the selected chip does not jump position as the alternates load.
  const currentManufacturer = detail?.manufacturer_code ?? null;
  const manufacturers = useMemo(() => {
    const seen = new Set<string>();
    if (currentManufacturer) seen.add(currentManufacturer);
    (alternates ?? []).forEach((a) => { if (a.manufacturer_code) seen.add(a.manufacturer_code); });
    return [...seen];
  }, [alternates, currentManufacturer]);

  const manufacturerOptions = useMemo<SearchableSelectOption[]>(
    () => manufacturers.map((code) => ({ value: code, label: code })),
    [manufacturers],
  );

  // Variants of the SELECTED manufacturer. Choosing a maker and choosing which of their SKUs are
  // two decisions; flattening them into one list of every variant from every manufacturer is what
  // made the raw alternates list unusable.
  //
  // The variant code is the second line rather than decoration: it is frequently the ONLY thing
  // separating two entries on screen (two colours of the same shingle truncate to the same
  // visible prefix at this width), and it is what the search box matches on when a name is too
  // long to type.
  const productOptions = useMemo<SearchableSelectOption[]>(() => {
    const mine = (alternates ?? []).filter((a) => a.manufacturer_code === currentManufacturer);
    const current = m?.product_variant_code
      ? [{
        product_variant_code: m.product_variant_code,
        manufacturer_code: currentManufacturer,
        variant_name: detail?.variant_name ?? m.product_variant_code,
      }]
      : [];
    const seen = new Set<string>();
    return [...current, ...mine]
      .filter((a) => !seen.has(a.product_variant_code) && seen.add(a.product_variant_code))
      .map((a) => ({
        value: a.product_variant_code,
        label: a.variant_name ?? a.product_variant_code,
        description: a.product_variant_code,
      }));
  }, [alternates, currentManufacturer, detail, m]);

  const supplierOptions = useMemo<SearchableSelectOption[]>(
    () => (lineSuppliers ?? []).map((sup) => ({
      value: sup.supplier_code,
      label: sup.supplier_name,
      description: sup.supplier_product_code,
      meta: sup.price !== null ? `${money(sup.price, currency)}/${sup.price_uom}` : null,
    })),
    [lineSuppliers, currency],
  );

  const swapTo = (variantCode: string) => commit(async () => {
    await swapMaterialLineVariant({
      estimate_material_line_code: lineCode, new_product_variant_code: variantCode,
    }).unwrap();
  });

  // A line the engine could not resolve to a variant — nothing satisfied the WBS resource's
  // required specifications, or nothing in the catalogue is priced. It has no name, no price and
  // no quantity, and rendering it as a bare material code with "— × 0" reads like a broken row
  // rather than the open question it is.
  // One sentence per state, naming BOTH what the option asked for and what the line actually got —
  // "Substituted" on its own is a label, not information a Sales Agent can act on.
  // The supplier list is only loaded once a row is open, and the sentence only shows then too, so
  // preferring the NAME over the code costs nothing and stops the line reading as "BECN".
  const actualName = (lineSuppliers ?? []).find((sup) => sup.supplier_code === m?.supplier_code)?.supplier_name
    ?? m?.supplier_code ?? detail?.manufacturer_code ?? '—';
  const conformanceText = (() => {
    switch (conformance) {
      case 'MATCHED':
        return t('estimates.editor.basisMatched', 'Priced from {{wanted}}, as the option asks.', { wanted: basisWanted ?? '' });
      case 'SUBSTITUTED':
        // A deviation has two very different causes and the fix differs: an engine fallback means
        // the basis could not be honoured, a hand-change means someone decided not to honour it.
        // Blaming the supplier for a line a user re-pointed themselves is simply untrue.
        return m?.selection_overridden_by
          ? t(
            'estimates.editor.basisOverridden',
            'Changed by hand to {{actual}}. This option is priced from {{wanted}}.',
            { wanted: basisWanted ?? '', actual: actualName },
          )
          : t(
            'estimates.editor.basisSubstituted',
            '{{wanted}} does not carry this item — priced from {{actual}} instead.',
            { wanted: basisWanted ?? '', actual: actualName },
          );
      case 'UNAVAILABLE':
        return t(
          'estimates.editor.basisUnavailable',
          'Nothing in the catalogue prices this item, so it cannot be quoted yet.',
        );
      default:
        return '';
    }
  })();

  // A custom line (price_source='MANUAL') was hand-typed via add_custom_estimate_material_line
  // and never meant to resolve against the catalogue — its material_code IS the name the user
  // gave it, not a placeholder for a missing product pick. Only a catalogue-origin line with no
  // product_variant_code is genuinely "unresolved".
  const isCustom = m?.price_source === 'MANUAL';
  const unresolved = !isCustom && !m?.product_variant_code;
  const title = line.description
    || detail?.variant_name
    || (unresolved
      ? t('estimates.editor.noItemResolved', 'No product chosen yet')
      : m?.material_code || lineCode);

  // Packaging is structured now (`units_per_package` base units per purchase unit), so it reads
  // as a real figure rather than the catalogue's old free text — which on 24 rows was an
  // editorial marker ("[VERIFY-CURRENT] confirm LF per bundle") and not a pack size at all.
  const baseUom = m?.base_uom ?? '';
  const packSize = detail?.units_per_package
    ? `${detail.units_per_package}${baseUom ? ` ${baseUom}` : ''}`
    : null;
  const purchaseUnit = [detail?.purchase_uom, packSize && `(${packSize})`]
    .filter(Boolean).join(' ');
  // Only the facts this line actually has. A variant states its traits as attributes, and the
  // absence of one is its absence from the list rather than a literal "None" / "N/A" string.
  const traits = (detail?.attributes ?? [])
    .map((a) => (a.value === null || a.value === undefined ? null : String(a.value)))
    .filter((v): v is string => !!v && !['none', 'n/a', '-', 'not applicable'].includes(v.trim().toLowerCase()))
    .join(' · ');

  return (
    <div
      id={lineCode}
      // The conformance colour lives on the row, not an 8px dot beside the price — see
      // CONFORMANCE_ROW. `title` moves here with it so the explanation survives losing the dot.
      title={conformance ? conformanceText : undefined}
      className={`rounded-xl border border-ui-border px-3 py-2 ${
        conformance ? CONFORMANCE_ROW[conformance] : 'bg-ui-card'
      }`}
    >
      {/* The disclosure arrow sits on the RIGHT, centred against the row — not leading the title.
          On the left it competed with the product name for the first thing the eye lands on, and
          its 9px glyph forced the second line to carry a matching `pl-4` just to stay aligned
          under a character. On the right it pairs with the line total as the row's controls. */}
      <button type="button" onClick={onToggle} className="flex w-full items-center justify-between gap-2 text-left">
        <span className="min-w-0">
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-xs font-semibold text-ui-text">{title}</span>
            {detail?.manufacturer_code && (
              <span className="flex-shrink-0 text-xs text-ui-text2">{detail.manufacturer_code}</span>
            )}
            {!unresolved && (
              <span className="truncate text-xs text-ui-text3">
                {money(m?.effective_unit_cost ?? m?.unit_cost, currency)} ×{' '}
                {m?.purchase_quantity ?? m?.effective_required_quantity ?? 0} {m?.purchase_uom ?? ''}
              </span>
            )}
          </span>
          {(detail?.product_name || unresolved) && (
            <span className="mt-0.5 flex items-center gap-1.5">
              {unresolved && <span className="text-xs text-ui-text3">{m?.material_code}</span>}
              {detail?.product_name && (
                <span className="text-xs text-ui-text3">{detail.product_name}</span>
              )}
            </span>
          )}
        </span>
        <span className="flex flex-shrink-0 items-center gap-2">
          {/* The row tint carries this visually; a colour alone is not readable, so the same
              sentence stays in the accessible name. */}
          {conformance && <span className="sr-only">{conformanceText}</span>}
          <span className="text-xs font-semibold text-ui-text">{money(m?.extended_cost, currency)}</span>
          <span className="text-xs text-ui-text3">{open ? '▼' : '▶'}</span>
        </span>
      </button>

      {open && (
        <div className="mt-2.5 space-y-2.5">
          {/* WHAT THIS LINE BUYS — the three decisions, each its own labelled control.
              They were chip rows, which worked for the two suppliers a line usually has and not
              at all for a manufacturer's product list: four wrapped rows of near-identical names
              you had to read end-to-end to tell apart. Manufacturer picks the maker, Product picks
              which of their items, Supplier prices it — same three decisions as before, same
              endpoints behind them. */}
          <div className="space-y-2">
            <div>
              <p className="text-xs text-ui-text3">{t('estimates.editor.product', 'Product')}</p>
              <div className="mt-1">
                <SearchableSelect
                  ariaLabel={t('estimates.editor.product', 'Product')}
                  // The options are keyed by VARIANT code (two colours of the same shingle are
                  // two variants of one product), so the selected value must be the variant too —
                  // matching on `product_code` found nothing and left every resolved line reading
                  // "No product chosen yet".
                  value={m?.product_variant_code ?? null}
                  options={productOptions}
                  disabled={readOnly}
                  placeholder={t('estimates.editor.noItemResolved', 'No product chosen yet')}
                  onChange={(variantCode) => {
                    if (variantCode && variantCode !== m?.product_variant_code) void swapTo(variantCode);
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div>
                <p className="text-xs text-ui-text3">{t('estimates.editor.supplier', 'Supplier')}</p>
                <div className="mt-1">
                  <SearchableSelect
                    ariaLabel={t('estimates.editor.supplier', 'Supplier')}
                    value={m?.supplier_code ?? null}
                    options={supplierOptions}
                    placeholder={t('estimates.editor.supplierAutomatic', 'Automatic — best available price')}
                    // Re-picking the current supplier clears it, handing the line back to
                    // automatic resolution rather than leaving it pinned with no way back.
                    allowClear
                    disabled={readOnly || supplierOptions.length === 0}
                    onChange={(supplierCode) => void commit(async () => {
                      await setLineSupplier({
                        estimate_material_line_code: lineCode,
                        supplier_code: supplierCode,
                      }).unwrap();
                    })}
                  />
                </div>
                {/* Says WHY the dot is the colour it is. A dot alone tells you something is off
                    without telling you what was asked for, which is the only part worth acting on. */}
                {conformance && conformance !== 'MATCHED' && (
                  <p className={`mt-1 rounded-md px-1.5 py-1 text-xs leading-relaxed ${CONFORMANCE_CLASS[conformance]}`}>
                    {conformanceText}
                  </p>
                )}
              </div>

              <div>
                <p className="text-xs text-ui-text3">
                  {t('estimates.editor.manufacturer', 'Manufacturer')}
                </p>
                <div className="mt-1">
                  <SearchableSelect
                    ariaLabel={t('estimates.editor.manufacturer', 'Manufacturer')}
                    value={currentManufacturer}
                    options={manufacturerOptions}
                    placeholder={t('estimates.editor.manufacturerNone', 'Not specified')}
                    disabled={readOnly || manufacturerOptions.length === 0}
                    onChange={(code) => {
                      if (!code || code === currentManufacturer) return;
                      const target = (alternates ?? []).find((a) => a.manufacturer_code === code);
                      if (target) void swapTo(target.product_variant_code);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* THE QUANTITY LADDER, in the order it is actually computed:
                required  -> (+ wastage)      = what the roof needs
                effective -> (/ pack size)    = what gets bought
              Reading left-to-right, top-to-bottom now follows the calculation, so the two derived
              cells underneath sit directly below the inputs that produce them. */}
          <div className="grid grid-cols-3 gap-x-2 gap-y-2 border-t border-ui-border pt-2.5 text-xs">
            <Field label={t('estimates.editor.requiredQty', 'Required qty')}>
              <input
                value={quantity}
                disabled={readOnly}
                onChange={(e) => setQuantity(e.target.value)}
                onBlur={() => {
                  if (quantity !== serverQuantity) {
                    void saveOverride({ calculated_quantity: Number(quantity) });
                  }
                }}
                inputMode="decimal"
                className="w-full rounded-md border border-ui-border bg-ui-card px-1.5 py-0.5 text-right text-xs text-ui-text focus:border-ui-ring focus:outline-none"
              />
            </Field>

            <Field label={t('estimates.editor.uom', 'UOM')}>
              <b className="block py-0.5 text-xs text-ui-text">{baseUom || '—'}</b>
            </Field>

            <Field label={t('estimates.editor.wastage', 'Wastage')}>
              <div className="flex items-center gap-1">
                <div className="flex flex-shrink-0 overflow-hidden rounded-md border border-ui-border">
                  {(['percent', 'fixed'] as const).map((mode) => (
                    <button
                      key={mode} type="button" disabled={readOnly} onClick={() => setWasteMode(mode)}
                      className={`px-1.5 py-0.5 text-xs font-semibold ${
                        wasteMode === mode ? 'bg-accent-fill text-on-accent' : 'bg-ui-card text-ui-text2'
                      }`}
                    >
                      {mode === 'percent' ? '%' : t('estimates.editor.fixed', 'Fixed')}
                    </button>
                  ))}
                </div>
                <input
                  value={wasteValue}
                  disabled={readOnly}
                  onChange={(e) => setWasteValue(e.target.value)}
                  onBlur={() => {
                    if (wasteValue === serverWasteValue) return;
                    void saveOverride(
                      wasteMode === 'fixed'
                        ? { waste_quantity: Number(wasteValue) }
                        : { waste_percent: Number(wasteValue) },
                    );
                  }}
                  inputMode="decimal"
                  className="w-full min-w-0 rounded-md border border-ui-border bg-ui-card px-1.5 py-0.5 text-right text-xs text-ui-text focus:border-ui-ring focus:outline-none"
                />
              </div>
            </Field>

            <Field label={t('estimates.editor.effectiveQty', 'Effective qty')}>
              <b className="block py-0.5 text-xs text-ui-text">{qty(m?.effective_required_quantity)}</b>
            </Field>

            <Field label={t('estimates.editor.purchaseUom', 'Purchase UOM')}>
              <b className="block py-0.5 text-xs text-ui-text" title={purchaseUnit || undefined}>
                {m?.purchase_uom ?? detail?.purchase_uom ?? '—'}
              </b>
            </Field>

            <Field label={t('estimates.editor.purchaseQty', 'Purchase qty')}>
              <b className="block py-0.5 text-xs text-ui-text">{qty(m?.purchase_quantity)}</b>
            </Field>
          </div>

          {/* Pack size is what makes the two rows above reconcile ("approx 25 LF/BDL"), so it sits
              under them rather than being crammed into the Purchase UOM cell. */}
          {packSize && (
            <p className="-mt-1 text-xs text-ui-text3">
              {t('estimates.editor.packSize', 'Pack size')}: {packSize}
            </p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-ui-border pt-2.5">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-ui-text3">{t('estimates.editor.unitPrice', 'Unit price')}</span>
              <input
                value={unitPrice}
                disabled={readOnly}
                onChange={(e) => setUnitPrice(e.target.value)}
                onBlur={() => {
                  if (unitPrice !== serverUnitPrice) void saveOverride({ unit_price: Number(unitPrice) });
                }}
                inputMode="decimal"
                className="w-20 rounded-md border border-ui-border bg-ui-card px-1.5 py-0.5 text-right text-xs text-ui-text focus:border-ui-ring focus:outline-none"
              />
              {m?.price_uom && <span className="text-xs text-ui-text3">/ {m.price_uom}</span>}
            </div>
            <b className="text-xs text-ui-text">{money(m?.extended_cost, currency)}</b>
          </div>

          {traits && <p className="text-xs leading-relaxed text-ui-text3">{traits}</p>}
        </div>
      )}
    </div>
  );
}
